import type { ApiClient } from './client';
import { uuid } from './client';
import type { UploadSlotResponse, UploadStatusResponse } from './types';

export interface UploadIntent {
  purpose: 'order_input' | 'voice_sample' | 'avatar_photo' | 'product_photo' | 'withdrawal_invoice' | 'reference' | 'video_input';
  kind: 'photo' | 'video' | 'audio' | 'document';
  origin: string;
  use?: 'style' | 'scene' | 'look';
  /** Для voice_sample сервер проверяет опубликованную версию до выдачи PUT URL. */
  consentVersion?: string;
  consentAccepted?: true;
}

/** Сохраняет ID slot при неопределённом результате PUT, чтобы UI мог продолжить опрос. */
export class UploadTransportError extends Error {
  constructor(
    public uploadId: string,
    message: string,
  ) {
    super(message);
    this.name = 'UploadTransportError';
  }
}

export class UploadRejectedError extends Error {
  constructor(public upload: UploadStatusResponse) {
    super(upload.reject_reason || upload.status);
    this.name = 'UploadRejectedError';
  }
}

const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    signal?.addEventListener('abort', abort, { once: true });
  });

export async function createUploadSlot(
  client: ApiClient,
  file: File,
  intent: UploadIntent,
  idempotencyKey = uuid(),
  signal?: AbortSignal,
): Promise<UploadSlotResponse> {
  if (intent.purpose === 'voice_sample' && (!intent.consentAccepted || !intent.consentVersion))
    throw new Error('voice_sample_consent_required');
  return client.request<UploadSlotResponse>('/app/uploads', {
    method: 'POST',
    idempotency: idempotencyKey,
    signal,
    body: {
      purpose: intent.purpose,
      kind: intent.kind,
      origin: intent.origin,
      ...(intent.use ? { use: intent.use } : {}),
      ...(intent.purpose === 'voice_sample'
        ? {
            consent_version: intent.consentVersion,
            consent_accepted: intent.consentAccepted,
          }
        : {}),
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
    },
  });
}

export async function sendUploadBytes(
  slot: UploadSlotResponse,
  file: File,
  signal?: AbortSignal,
  backendToken?: string | null,
): Promise<void> {
  if (Date.parse(slot.put_expires_at) <= Date.now()) throw new UploadTransportError(slot.id, 'upload_url_expired');
  // Fetch сам выставляет Content-Length. Bearer нужен только для собственного Backend upload API.
  const headers = { ...slot.put_headers };
  delete headers.Authorization;
  delete headers.authorization;
  delete headers['Content-Length'];
  delete headers['content-length'];
  if (backendToken) headers.Authorization = `Bearer ${backendToken}`;
  let response: Response;
  try {
    response = await fetch(slot.put_url, { method: 'PUT', headers, body: file, signal });
  } catch {
    // Сеть могла оборваться уже после записи объекта; повтор PUT может заменить staging при проверке.
    throw new UploadTransportError(slot.id, 'upload_put_outcome_unknown');
  }
  if (!response.ok) throw new UploadTransportError(slot.id, `upload_put_failed_${response.status}`);
}

export async function waitForUpload(
  client: ApiClient,
  uploadId: string,
  signal?: AbortSignal,
  maxWaitMs = 120_000,
): Promise<UploadStatusResponse> {
  const deadline = Date.now() + maxWaitMs;
  for (;;) {
    const upload = await client.request<UploadStatusResponse>(`/app/uploads/${encodeURIComponent(uploadId)}`, { signal });
    if (upload.status === 'ready') return upload;
    if (upload.status === 'rejected' || upload.status === 'expired' || upload.status === 'validation_failed')
      throw new UploadRejectedError(upload);
    if (Date.now() >= deadline) throw new UploadTransportError(uploadId, 'upload_validation_pending');
    await delay(Math.min(Math.max(upload.poll_after_ms || 1000, 250), 5000), signal);
  }
}

export async function uploadFile(
  client: ApiClient,
  file: File,
  intent: UploadIntent,
  options: { idempotencyKey?: string; signal?: AbortSignal } = {},
): Promise<UploadStatusResponse> {
  const slot = await createUploadSlot(client, file, intent, options.idempotencyKey, options.signal);
  // Повтор создания slot может уже вернуть ready. Завершённый объект не перезаписывается.
  if (slot.status !== 'pending' && slot.status !== 'processing') return waitForUpload(client, slot.id, options.signal);
  if (!slot.put_url) throw new UploadTransportError(slot.id, 'upload_validation_pending');
  await sendUploadBytes(
    { ...slot, put_url: client.resolveUploadUrl(slot.put_url) },
    file,
    options.signal,
    client.uploadTokenFor(slot.put_url),
  );
  return waitForUpload(client, slot.id, options.signal);
}
